/**
 * Christmas mug designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasMugDesignsPack: ToilePack = {
    slug: 'christmas-mug-designs',
    category: 'season',
    name: 'Christmas mug designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Cocoa slogan', subject: 'the words "HOT COCOA SEASON" lettered in a rounded script beside a small mug motif'},
        {key: 'snowman', label: 'Snowman', subject: 'a cheerful snowman with a scarf and a top hat, no text'},
        {key: 'band', label: 'Pattern band', subject: 'a horizontal band of small trees and stars sized to wrap a mug, no text'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['a cat instead', 'for a teacher gift', 'as a wrap-around band only'],
    directionHint: 'try: a cat instead',
    seo: {
        title: 'Christmas Mug Designs: Cocoa, Snowman, Pattern',
        metaDescription: 'Nine christmas mug designs -- cocoa slogan, snowman, pattern band -- in scandi folk, vintage card, and cozy cartoon finishes. Flat art, no mug.',
        h1: 'Christmas mug design: cocoa slogan, snowman, and pattern band',
        tagline: 'Nine wraps, three mug designs, three finishes each.',
        intro: "Nine flat christmas mug designs -- a 'Hot Cocoa Season' slogan, a cheerful snowman, and a pattern band sized to wrap a mug -- with no mug mockup in frame. Three finishes: scandi folk, vintage card, and cozy cartoon. Built for sublimation printing, decals, or a print-on-demand mug shop. Ask for a cat instead, or a wrap-around band only, and the same three finishes carry it.",
        useCases: [
            'a sublimation mug print',
            'a print-on-demand mug shop',
            'a teacher or coworker gift mug',
            'a mug decal design',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine flat art designs -- a cocoa slogan, a snowman, and a wrap-band pattern -- each in scandi folk, vintage card, and cozy cartoon finishes, with no mug in the image.'},
            {q: 'Is there a mug mockup included?', a: 'No -- every design is flat, isolated art meant to wrap onto your own mug template or sublimation blank.'},
            {q: 'What size mug does the pattern band fit?', a: 'The band is sized for a standard 11oz mug wrap; resize it slightly for a 15oz mug or a travel tumbler.'},
            {q: 'Can I sell mugs printed with these designs?', a: 'Yes -- the designs are yours to print and sell, including on a print-on-demand storefront.'},
        ],
    },
}
